import { onMounted, onUnmounted, reactive, ref, toValue, watch } from 'vue';
import { isBrowser } from '@nzyme/dom-utils/isBrowser.js';
import { asArray } from '@nzyme/utils/array/asArray.js';
import { makeRef } from './reactivity/makeRef.js';
import { unwrapElement } from './unwrapElement.js';
/** Tracks element visibility using IntersectionObserver with reactive state. */
export function useIntersectionObserver(options) {
    const intersectionRatio = ref(0);
    const isIntersecting = ref(false);
    const isFullyInView = ref(false);
    const isSupported = isBrowser() && 'IntersectionObserver' in window;
    const isReadyIntersection = ref(false);
    const element = makeRef(options.element);
    let observer;
    if (isSupported) {
        onMounted(init);
    }
    onUnmounted(pause);
    watch(element, (el, prevEl) => {
        unobserve(prevEl);
        observe(el);
    });
    function init() {
        observer = new IntersectionObserver(entries => {
            // Sometimes we get the same entry multiple times
            const seen = new Set();
            for (const entry of entries) {
                if (seen.has(entry.target)) {
                    continue;
                }
                seen.add(entry.target);
                intersectionRatio.value = entry.intersectionRatio;
                if (entry.intersectionRatio > 0) {
                    isIntersecting.value = true;
                    isFullyInView.value = entry.intersectionRatio >= 1;
                    void options.onVisible?.(entry.target);
                }
                else {
                    void options.onHidden?.(entry.target);
                }
            }
            isReadyIntersection.value = true;
        }, toValue(options.options));
        if (options.immediate !== false) {
            start();
        }
    }
    function start() {
        observe(element.value);
    }
    function pause() {
        unobserve(element.value);
    }
    function observe(el) {
        if (!observer || !el) {
            return;
        }
        const elements = asArray(el);
        for (const item of elements) {
            observer.observe(unwrapElement(item));
        }
    }
    function unobserve(el) {
        if (!observer || !el) {
            return;
        }
        const elements = asArray(el);
        for (const item of elements) {
            observer.unobserve(unwrapElement(item));
        }
    }
    return reactive({
        intersectionRatio,
        isSupported,
        isIntersecting,
        isFullyInView,
        isReadyIntersection,
        start,
        pause,
        observe,
        unobserve,
    });
}
//# sourceMappingURL=useIntersectionObserver.js.map